/**
 * Cognito sign-in for the SPA (D5): **SRP** against the stage's SPA app
 * client — the password never leaves the browser — and REFRESH_TOKEN_AUTH to
 * renew.
 *
 * - **SRP** is `amazon-cognito-identity-js` (the smallest maintained library
 *   that does the SRP maths and the PASSWORD_VERIFIER round trip). It is
 *   injected as `SrpLib` — `realSrpLib` below wires the library — so the
 *   callback-to-outcome mapping is tested against a fake. The library keeps
 *   tokens in an in-memory storage we hand it; persistence is ours
 *   (`session.ts`).
 * - **Refresh** is one unauthenticated JSON POST to the Cognito endpoint
 *   (`InitiateAuth`, `REFRESH_TOKEN_AUTH`), over the injected fetch.
 */
import type { FetchPort } from '@norna/api-client/ports.js';
import { AuthenticationDetails, CognitoUser, CognitoUserPool, type ICognitoStorage } from 'amazon-cognito-identity-js';
import { z } from 'zod';
import { type Session, sessionFromTokens } from './session.js';

export type CognitoConfig = { readonly region: string; readonly userPoolId: string; readonly clientId: string };

/** What the library's session object offers, as far as we read it. */
export type SrpSessionLike = {
  getIdToken(): { getJwtToken(): string };
  getAccessToken(): { getJwtToken(): string };
  getRefreshToken(): { getToken(): string };
};

export type SrpCallbacks = {
  onSuccess: (session: SrpSessionLike) => void;
  onFailure: (err: unknown) => void;
  newPasswordRequired: (userAttributes: unknown, requiredAttributes: unknown) => void;
  // The challenges the SPA does not support yet. The library calls these
  // unguarded, so each must exist, or a pool user with MFA meets a TypeError
  // (or a promise that never settles) instead of an explanation.
  mfaRequired: () => void;
  totpRequired: () => void;
  mfaSetup: () => void;
  selectMFAType: () => void;
  customChallenge: () => void;
};

export type SrpUserLike = {
  authenticateUser(details: AuthenticationDetails, callbacks: SrpCallbacks): void;
  completeNewPasswordChallenge(newPassword: string, requiredAttributeData: Record<string, string>, callbacks: SrpCallbacks): void;
};

export type SrpLib = {
  readonly user: (email: string) => SrpUserLike;
  readonly details: (email: string, password: string) => AuthenticationDetails;
};

export type LoginOutcome =
  | { readonly type: 'signed-in'; readonly session: Session }
  | { readonly type: 'new-password-required'; readonly complete: (newPassword: string) => Promise<LoginOutcome> };

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const message: unknown = Reflect.get(err, 'message');
    if (typeof message === 'string') return message;
  }
  return String(err);
}

function unsupported(challenge: string): Error {
  return new Error(`This account needs ${challenge}, which the Norna web app does not support yet. Ask an administrator.`);
}

function sessionOf(result: SrpSessionLike): Session {
  const session = sessionFromTokens('cognito', result.getIdToken().getJwtToken(), result.getAccessToken().getJwtToken(), result.getRefreshToken().getToken());
  if (session === null) throw new Error('Cognito answered an ID token without the claims the SPA needs');
  return session;
}

/** Sign in with SRP. Rejects with Cognito's message (wrong password, unknown user, …). */
export function cognitoLogin(lib: SrpLib, email: string, password: string): Promise<LoginOutcome> {
  const user = lib.user(email);
  const settle = (run: (callbacks: SrpCallbacks) => void): Promise<LoginOutcome> =>
    new Promise<LoginOutcome>((resolve, reject) => {
      run({
        onSuccess: (result) => {
          try {
            resolve({ type: 'signed-in', session: sessionOf(result) });
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        },
        onFailure: (err) => reject(new Error(errorMessage(err))),
        mfaRequired: () => reject(unsupported('SMS MFA')),
        totpRequired: () => reject(unsupported('TOTP MFA')),
        mfaSetup: () => reject(unsupported('MFA setup')),
        selectMFAType: () => reject(unsupported('MFA type selection')),
        customChallenge: () => reject(unsupported('a custom challenge')),
        newPasswordRequired: () =>
          resolve({
            type: 'new-password-required',
            // An admin-created user's first sign-in: the pool asks for a new password.
            complete: (newPassword) => settle((callbacks) => user.completeNewPasswordChallenge(newPassword, {}, callbacks)),
          }),
      });
    });
  return settle((callbacks) => user.authenticateUser(lib.details(email, password), callbacks));
}

/** A storage that forgets: the library must not write tokens to localStorage behind `session.ts`'s back. */
export function memoryStorage(): ICognitoStorage {
  const data = new Map<string, string>();
  return {
    getItem: (key) => data.get(key) ?? null,
    setItem: (key, value) => {
      data.set(key, value);
    },
    removeItem: (key) => {
      data.delete(key);
    },
    clear: () => {
      data.clear();
    },
  };
}

/** The real library, for `config`'s pool and SPA client. */
export function realSrpLib(config: CognitoConfig): SrpLib {
  const storage = memoryStorage();
  const pool = new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.clientId, Storage: storage });
  return {
    user: (email) => new CognitoUser({ Username: email, Pool: pool, Storage: storage }),
    details: (email, password) => new AuthenticationDetails({ Username: email, Password: password }),
  };
}

const refreshResponseSchema = z
  .object({
    AuthenticationResult: z.object({ IdToken: z.string().min(1), AccessToken: z.string().min(1) }).loose(),
  })
  .loose();

/** Renew with the refresh token. Null when there is none, or Cognito refuses it (revoked, expired). */
/**
 * Cognito's refusals of a refresh token: the session is over and the user
 * signs in again. Anything else — throttling, a 5xx, the network — is
 * transient and THROWS, so the session store keeps the session (session.ts).
 */
const REFUSALS = new Set(['NotAuthorizedException', 'UserNotFoundException', 'UserNotConfirmedException', 'PasswordResetRequiredException']);

const errorTypeSchema = z.object({ __type: z.string() }).loose();

/** Renew with the refresh token: the renewed session, null when Cognito refuses it, or a rejection when the attempt failed transiently. */
export async function refreshCognitoSession(fetch: FetchPort, config: CognitoConfig, session: Session): Promise<Session | null> {
  if (session.refreshToken === null) return null;
  const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-amz-json-1.1', 'x-amz-target': 'AWSCognitoIdentityProviderService.InitiateAuth' },
    body: JSON.stringify({ AuthFlow: 'REFRESH_TOKEN_AUTH', ClientId: config.clientId, AuthParameters: { REFRESH_TOKEN: session.refreshToken } }),
  });
  const text = await response.text();
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    json = null;
  }
  if (response.status !== 200) {
    const type = errorTypeSchema.safeParse(json);
    // The type may be namespaced (`…#NotAuthorizedException`).
    const name = type.success ? (type.data.__type.split('#').pop() ?? '') : '';
    if (response.status === 400 && REFUSALS.has(name)) return null;
    throw new Error(`Cognito refresh failed (HTTP ${String(response.status)}${name === '' ? '' : ` ${name}`}); the session is kept`);
  }
  const parsed = refreshResponseSchema.safeParse(json);
  if (!parsed.success) throw new Error('Cognito answered a refresh without tokens');
  const result = parsed.data.AuthenticationResult;
  // Cognito keeps the original refresh token valid and does not send a new one.
  return sessionFromTokens('cognito', result.IdToken, result.AccessToken, session.refreshToken);
}
